/**
 * ReviewForm — Premium review submission component
 * Features: Star ratings, photo upload, dish tags, visit date
 */
import React, { useState } from 'react';
import { X, Camera, Calendar, Plus, Star, Loader2 } from 'lucide-react';
import { Button, RatingInput } from '../../shared';

const CATEGORY_LABELS = {
  food: { en: 'Food Quality', th: 'คุณภาพอาหาร' },
  service: { en: 'Service', th: 'การบริการ' },
  atmosphere: { en: 'Atmosphere', th: 'บรรยากาศ' },
  value: { en: 'Value for Money', th: 'ความคุ้มค่า' }
};

export function ReviewForm({
  restaurantId,
  restaurantName,
  onSubmit,
  onCancel,
  language = 'th',
  t = (key) => key
}) {
  const [ratings, setRatings] = useState({
    overall: 0,
    food: 0,
    service: 0,
    atmosphere: 0,
    value: 0
  });
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [photos, setPhotos] = useState([]);
  const [visitedDate, setVisitedDate] = useState('');
  const [selectedDishes, setSelectedDishes] = useState([]);
  const [newDish, setNewDish] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const handleRatingChange = (category, value) => {
    setRatings(prev => ({ ...prev, [category]: value }));
    // Clear error when rating is set
    if (value > 0 && errors[category]) {
      setErrors(prev => ({ ...prev, [category]: null }));
    }
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files || []);
    const newPhotos = files.map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }));
    setPhotos(prev => [...prev, ...newPhotos].slice(0, 5)); // Max 5 photos
  };

  const removePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const addDish = () => {
    if (newDish.trim() && !selectedDishes.includes(newDish.trim())) {
      setSelectedDishes(prev => [...prev, newDish.trim()]);
      setNewDish('');
    }
  };

  const removeDish = (dish) => {
    setSelectedDishes(prev => prev.filter(d => d !== dish));
  };

  const validate = () => {
    const newErrors = {};
    if (ratings.overall === 0) {
      newErrors.overall = language === 'th' ? 'กรุณาให้คะแนน' : 'Please provide a rating';
    }
    if (!title.trim()) {
      newErrors.title = language === 'th' ? 'กรุณาใส่หัวข้อ' : 'Please add a title';
    }
    if (!content.trim() || content.length < 20) {
      newErrors.content = language === 'th' ? 'กรุณาเขียนรีวิวอย่างน้อย 20 ตัวอักษร' : 'Review must be at least 20 characters';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      await onSubmit?.({
        restaurantId,
        rating: ratings.overall,
        categoryRatings: {
          food: ratings.food,
          service: ratings.service,
          atmosphere: ratings.atmosphere,
          value: ratings.value
        },
        title: title.trim(),
        content: content.trim(),
        dishes: selectedDishes,
        images: photos.map(p => p.file),
        visitDate: visitedDate || new Date().toISOString().split('T')[0]
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Overall Rating */}
      <div>
        <label className="block text-[14px] font-semibold text-neutral-800 mb-3">
          {language === 'th' ? 'คะแนนโดยรวม' : 'Overall Rating'} *
        </label>
        <div className="flex items-center gap-4">
          <RatingInput
            value={ratings.overall}
            onChange={(v) => handleRatingChange('overall', v)}
            size="xl"
          />
          {errors.overall && (
            <span className="text-red-500 text-[13px]">{errors.overall}</span>
          )}
        </div>
      </div>

      {/* Category Ratings */}
      <div className="grid grid-cols-2 gap-4">
        {Object.entries(CATEGORY_LABELS).map(([key, labels]) => (
          <div key={key}>
            <label className="block text-[13px] font-medium text-neutral-600 mb-2">
              {labels[language] || labels.en}
            </label>
            <RatingInput
              value={ratings[key]}
              onChange={(v) => handleRatingChange(key, v)}
              size="md"
            />
          </div>
        ))}
      </div>

      {/* Title */}
      <div>
        <label className="block text-[14px] font-semibold text-neutral-800 mb-2">
          {language === 'th' ? 'หัวข้อรีวิว' : 'Review Title'} *
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={language === 'th' ? 'สรุปประสบการณ์ของคุณ...' : 'Summarize your experience...'}
          className={`w-full px-4 py-3 rounded-xl border ${errors.title ? 'border-red-300' : 'border-neutral-200'} focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 outline-none transition-all text-[14px]`}
          maxLength={100}
        />
        {errors.title && (
          <span className="text-red-500 text-[12px] mt-1">{errors.title}</span>
        )}
      </div>

      {/* Content */}
      <div>
        <label className="block text-[14px] font-semibold text-neutral-800 mb-2">
          {language === 'th' ? 'รีวิวของคุณ' : 'Your Review'} *
        </label>
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder={language === 'th' ? 'เล่าประสบการณ์ของคุณให้คนอื่นฟัง...' : 'Share your experience with others...'}
          rows={4}
          className={`w-full px-4 py-3 rounded-xl border ${errors.content ? 'border-red-300' : 'border-neutral-200'} focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 outline-none transition-all text-[14px] resize-none`}
          maxLength={1000}
        />
        <div className="flex justify-between mt-1">
          {errors.content && (
            <span className="text-red-500 text-[12px]">{errors.content}</span>
          )}
          <span className="text-[12px] text-neutral-400 ml-auto">
            {content.length}/1000
          </span>
        </div>
      </div>

      {/* Dishes */}
      <div>
        <label className="block text-[14px] font-semibold text-neutral-800 mb-2">
          {language === 'th' ? 'เมนูที่สั่ง' : 'Dishes Ordered'}
        </label>
        <div className="flex flex-wrap gap-2 mb-2">
          {selectedDishes.map(dish => (
            <span
              key={dish}
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-neutral-100 rounded-full text-[13px] text-neutral-700"
            >
              {dish}
              <button
                type="button"
                onClick={() => removeDish(dish)}
                className="text-neutral-400 hover:text-neutral-600"
              >
                <X size={14} />
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={newDish}
            onChange={(e) => setNewDish(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addDish())}
            placeholder={language === 'th' ? 'เพิ่มเมนู...' : 'Add a dish...'}
            className="flex-1 px-4 py-2.5 rounded-xl border border-neutral-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 outline-none transition-all text-[14px]"
          />
          <Button
            type="button"
            variant="secondary"
            onClick={addDish}
            icon={<Plus size={16} />}
          >
            {language === 'th' ? 'เพิ่ม' : 'Add'}
          </Button>
        </div>
      </div>

      {/* Photos */}
      <div>
        <label className="block text-[14px] font-semibold text-neutral-800 mb-2">
          {language === 'th' ? 'รูปภาพ' : 'Photos'} ({language === 'th' ? 'ไม่บังคับ' : 'Optional'})
        </label>
        <div className="flex gap-3 overflow-x-auto pb-2">
          {photos.map((photo, index) => (
            <div
              key={index}
              className="relative flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden group"
            >
              <img
                src={photo.preview}
                alt=""
                className="w-full h-full object-cover"
              />
              <button
                type="button"
                onClick={() => removePhoto(index)}
                className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity"
              >
                <X size={20} className="text-white" />
              </button>
            </div>
          ))}
          {photos.length < 5 && (
            <label className="flex-shrink-0 w-20 h-20 rounded-xl border-2 border-dashed border-neutral-300 flex flex-col items-center justify-center cursor-pointer hover:border-brand-500 hover:bg-brand-50 transition-colors">
              <Camera size={20} className="text-neutral-400" />
              <span className="text-[11px] text-neutral-500 mt-1">
                {language === 'th' ? 'เพิ่ม' : 'Add'}
              </span>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handlePhotoUpload}
                className="hidden"
              />
            </label>
          )}
        </div>
        <span className="text-[12px] text-neutral-400">
          {language === 'th' ? 'สูงสุด 5 รูป' : 'Max 5 photos'}
        </span>
      </div>

      {/* Visit Date */}
      <div>
        <label className="block text-[14px] font-semibold text-neutral-800 mb-2">
          {language === 'th' ? 'วันที่ไป' : 'Visit Date'}
        </label>
        <div className="relative">
          <Calendar size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input
            type="date"
            value={visitedDate}
            onChange={(e) => setVisitedDate(e.target.value)}
            max={new Date().toISOString().split('T')[0]}
            className="w-full pl-11 pr-4 py-3 rounded-xl border border-neutral-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 outline-none transition-all text-[14px]"
          />
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-4">
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          fullWidth
        >
          {language === 'th' ? 'ยกเลิก' : 'Cancel'}
        </Button>
        <Button
          type="submit"
          variant="primary"
          fullWidth
          loading={submitting}
          disabled={ratings.overall === 0 || !title.trim() || !content.trim()}
        >
          {submitting
            ? (language === 'th' ? 'กำลังส่ง...' : 'Submitting...')
            : (language === 'th' ? 'ส่งรีวิว' : 'Submit Review')
          }
        </Button>
      </div>
    </form>
  );
}

export default ReviewForm;
